import { Badge, Typography, Card, CardContent, CardActionArea, Divider, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { User } from "@domain/Auth";
import React from "react";
import { UserContext } from "@gui/contexts/user/UserContext";
import { ISSUE_COUNT_PATHS, useIssueCounts } from "@gui/contexts/issueCounts/IssueCountsContext";
import { NavLink, USER_LINKS, ALL_ADMIN_LINKS, DEVELOPER_LINKS } from "@gui/widgets/Toolbar";

interface LandingPageProps {
	setLoggedIn: (user: User) => void;
}

/**
 * Stable E2E hook for a landing-grid card, derived from its label so a spec can name a card
 * the way a user sees it: "Company Settings" -> `home-card-company-settings`.
 *
 * The card labels come from `navLinks.tsx` and are unique across USER/ADMIN/DEVELOPER links,
 * so the ids are unique too. This is what lets `e2e/lib/home.ts` stop disambiguating cards
 * from the sidebar's identically-labeled entries by `<h6>`-vs-`<span>` DOM shape.
 */
function homeCardTestId(label: string): string {
	return `home-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

// oxlint-disable-next-line no-unused-vars
export default function HomePage(props: LandingPageProps) {
	const navigate = useNavigate();
	const user = React.useContext(UserContext) as User | undefined;
	const { counts: issueCounts, loading: loadingCounts } = useIssueCounts();

	function linkCard(link: NavLink) {
		const hasFetcher = ISSUE_COUNT_PATHS.has(link.to);
		const count = issueCounts[link.to];
		// Same three-state pattern as the sidebar badge:
		//  - in flight → CircularProgress as badgeContent
		//  - resolved > 0 → numeric count
		//  - resolved 0 (or no fetcher for this row) → no Badge wrapper at all
		const isFetching = hasFetcher && count === undefined && loadingCounts;
		const showCountBadge = hasFetcher && count !== undefined && count > 0;
		const icon = isFetching ? (
			<Badge
				badgeContent={<CircularProgress size={10} thickness={6} sx={{ color: "inherit" }} />}
				color="error"
				overlap="circular"
			>
				{link.icon}
			</Badge>
		) : showCountBadge ? (
			<Badge badgeContent={count} color="error" overlap="circular">
				{link.icon}
			</Badge>
		) : (
			link.icon
		);

		return (
			<Grid key={link.to} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
				<Card className="home-card" data-testid={homeCardTestId(link.text)}>
					<CardActionArea onClick={() => navigate(link.to)}>
						<CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
							{icon}
							<Typography variant="h6">{link.text}</Typography>
						</CardContent>
					</CardActionArea>
				</Card>
			</Grid>
		);
	}

	// Skip the "Home" link on the home page itself
	const pageLinks = USER_LINKS.filter((link) => link.to !== "/home");

	return (
		<>
			<Grid container spacing={2} className="home-grid">
				{pageLinks.map(linkCard)}
			</Grid>
			{user?.isAdmin && (
				<>
					<Divider sx={{ mx: 2, mb: 2, mt: 2 }} />
					<Grid container spacing={2} className="home-grid">
						{ALL_ADMIN_LINKS.map(linkCard)}
					</Grid>
				</>
			)}
			{/* {user?.isSoftwareTester && (
				<>
					<Divider sx={{ mx: 2, mb: 2, mt: 2 }} />
					<Grid container spacing={2} className="home-grid">
						{SOFTWARE_TESTER_LINKS.map(linkCard)}
					</Grid>
				</>
			)} */}
			{user?.isDeveloper && (
				<>
					<Divider sx={{ mx: 2, mb: 2, mt: 2 }} />
					<Grid container spacing={2} className="home-grid">
						{DEVELOPER_LINKS.map(linkCard)}
					</Grid>
				</>
			)}
		</>
	);
}
